<template>
  <div class="date-range-component">
    <!-- Label -->
    <label v-if="label" :for="elemId" class="form-label">
      {{ label }}
    </label>
    
    <!-- Date Range Input Container -->
    <div class="date-range-input-container" :class="{ 'is-disabled': isDisabled, 'is-invalid': !hasValidation }">
      <div class="date-range-icon">
        <i class="pi" :class="icon"></i>
      </div>
      
      <!-- Display Input (shows formatted date range) -->
      <input
        :id="elemId"
        ref="displayInput"
        type="text"
        class="date-range-display"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :required="isRequired"
        :data-testid="`${elemId}-display`"
        readonly
        @click="handleInputClick"
        @keydown="handleKeydown"
      />

      <!-- Dropdown Toggle -->
      <button
        type="button"
        class="date-range-toggle"
        :disabled="isDisabled"
        :data-testid="`${elemId}-toggle`"
        @click="handleToggleClick"
        tabindex="-1"
      >
        <i class="pi pi-chevron-down" :class="{ 'pi-chevron-up': isPickerVisible }"></i>
      </button>
    </div>
    
    <!-- Date Picker Dropdown -->
    <div
      v-if="isPickerVisible"
      ref="datePicker"
      class="date-picker-dropdown"
      @click.stop
    >
      <!-- Quick Actions / Predefined Buttons -->
      <div v-if="showPredefinedButtons && predefinedButtons && predefinedButtons.length > 0" class="predefined-buttons">
        <app-button
          v-for="button in predefinedButtons"
          :key="button.period"
          type="button"
          :label="button.label"
          severity="warn"
          size="small"
          outlined
          :data-testid="`${elemId}-preset-${button.period}`"
          @click="setPredefinedDate(button.period)"
        />
      </div>
      
      <!-- Date Inputs -->
      <div class="date-inputs">
        <div class="date-input-group">
          <label class="date-input-label">{{ t('De') }}</label>
          <input
            ref="startDateInput"
            type="date"
            class="date-input"
            :data-testid="`${elemId}-start-date`"
            :value="startDateValue"
            @input="updateStartDate"
            @change="updateStartDate"
          />
        </div>

        <div class="date-separator">-</div>

        <div class="date-input-group">
          <label class="date-input-label">{{ t('Até') }}</label>
          <input
            ref="endDateInput"
            type="date"
            class="date-input"
            :data-testid="`${elemId}-end-date`"
            :value="endDateValue"
            @input="updateEndDate"
            @change="updateEndDate"
          />
        </div>
      </div>
      
      <!-- Action Buttons -->
      <div class="action-buttons">
        <app-button
          type="button"
          :label="t('Aplicar')"
          icon="pi pi-check"
          severity="primary"
          size="small"
          :disabled="!startDateValue || !endDateValue"
          :data-testid="`${elemId}-apply`"
          @click="hideDatePicker"
        />
      </div>
    </div>
    
    <!-- Validation Message -->
    <div v-if="!hasValidation && isRequired" class="validation-message">
      {{ t('Campo obrigatório') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../composables/useDateFormatter';

interface PredefinedButton {
  period: string;
  label: string;
}

const { t } = useI18n();

const props = defineProps({
  elemId: {
    type: String,
    default: () => `date-range-${Math.random().toString(36).substr(2, 9)}`
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: () => 'Selecione o período'
  },
  modelValue: {
    type: String,
    default: ''
  },
  // Backward compatibility
  value: {
    type: String,
    default: ''
  },
  predefinedButtons: {
    type: Array as () => PredefinedButton[] | null,
    default: null // null means no predefined buttons, empty array also means no buttons
  },
  showPredefinedButtons: {
    type: Boolean,
    default: true // Control visibility even when predefinedButtons is provided
  },
  icon: {
    type: String,
    default: 'pi-calendar'
  },
  validation: {
    type: Function as any,
    default: () => true
  },
  ngRequired: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'input': [value: string];
  'change': [value: string];
}>();

// Use date formatter composable
const { formatDate: formatDateI18n, parseBackendDate, fromDateToApiDate } = useDateFormatter()

// Reactive refs
const isPickerVisible = ref<boolean>(false);
const startDateValue = ref<string>('');
const endDateValue = ref<string>('');
const displayInput = ref<HTMLElement | null>(null);
const datePicker = ref<HTMLElement | null>(null);
const startDateInput = ref<HTMLInputElement | null>(null);
const endDateInput = ref<HTMLInputElement | null>(null);

// Computed properties
const currentValue = computed((): string => props.modelValue || props.value || '');

const isRequired = computed((): boolean =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  (props.validation as () => boolean)()
);

// v-model contract: ISO `YYYY-MM-DD - YYYY-MM-DD`. The display <input> shows
// the locale-formatted version (formatDateI18n) so the user still sees their
// local format, but the value flowing in/out of the component is timezone-
// and locale-independent.
const displayValue = computed((): string => {
  const value = currentValue.value;
  if (!value || !value.includes(' - ')) return '';
  const [start, end] = value.split(' - ');
  const s = parseDate(start);
  const e = parseDate(end);
  if (!s || !e) return '';
  return `${formatDate(s)} - ${formatDate(e)}`;
});

// Date formatting utilities (using i18n)
const formatDate = (date: Date): string => {
  if (!date || !(date instanceof Date)) return '';
  return formatDateI18n(date);
};

// Parses ISO `YYYY-MM-DD` (the v-model contract). Returns null on bad input.
const parseDate = (dateString: string): Date | null => {
  if (!dateString || typeof dateString !== 'string') return null;
  const m = dateString.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
};

const dateToInputValue = (date: Date): string => {
  if (!date || !(date instanceof Date)) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const inputValueToDate = (inputValue: string): Date | null => {
  if (!inputValue) return null;
  const date = new Date(inputValue + 'T00:00:00');
  return isNaN(date.getTime()) ? null : date;
};

// Date range management
const updateDateInputs = (): void => {
  if (currentValue.value) {
    const dates = currentValue.value.split(' - ');
    if (dates.length === 2) {
      const startDate = parseDate(dates[0]);
      const endDate = parseDate(dates[1]);

      if (startDate && endDate) {
        startDateValue.value = dateToInputValue(startDate);
        endDateValue.value = dateToInputValue(endDate);
        return;
      }
    }
  }

  // Set default values (30 days ago to today)
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  startDateValue.value = dateToInputValue(thirtyDaysAgo);
  endDateValue.value = dateToInputValue(today);
};

const updateStartDate = (event: Event): void => {
  startDateValue.value = (event.target as HTMLInputElement).value;
  // Immediately emit the change
  emitDateChange();
};

const updateEndDate = (event: Event): void => {
  endDateValue.value = (event.target as HTMLInputElement).value;
  // Immediately emit the change
  emitDateChange();
};

const emitDateChange = (): void => {
  if (!startDateValue.value || !endDateValue.value) return;

  const startDate = inputValueToDate(startDateValue.value);
  const endDate = inputValueToDate(endDateValue.value);

  if (!startDate || !endDate) return;

  // Ensure start date is not after end date
  if (startDate > endDate) {
    [startDateValue.value, endDateValue.value] = [endDateValue.value, startDateValue.value];
    const tempDate = startDate;
    startDate.setTime(endDate.getTime());
    endDate.setTime(tempDate.getTime());
  }

  // Emit ISO `YYYY-MM-DD - YYYY-MM-DD`. The display <input> renders the
  // locale-formatted version via `displayValue` so the user still sees their
  // local format.
  const isoValue = dateToInputValue(startDate) + ' - ' + dateToInputValue(endDate);

  emit('update:modelValue', isoValue);
  emit('input', isoValue);
  emit('change', isoValue);
};

// Predefined date ranges
const setPredefinedDate = (period: string): void => {
  const today = new Date();
  let startDate = new Date();

  switch(period) {
    case 'today':
      startDate = new Date();
      break;
    case 'yesterday':
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 1);
      break;
    case '7days':
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      break;
    case '30days':
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      break;
    case 'thismonth':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      break;
    case 'lastmonth':
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      break;
    case '2years':
      startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 2);
      break;
    case '1.5years':
      startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 18);
      break;
    case '1year':
      startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
    default:
      return;
  }

  let endDate = today;

  // Set specific end dates for different periods
  if (period === 'today' || period === 'yesterday') {
    // For single day periods, set both dates to the same day
    endDate = new Date(startDate);
  } else if (period === 'thismonth') {
    // For current month, set end date to last day of current month
    endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  } else if (period === 'lastmonth') {
    // For last month, set end date to last day of last month
    endDate = new Date(today.getFullYear(), today.getMonth(), 0);
  }

  startDateValue.value = dateToInputValue(startDate);
  endDateValue.value = dateToInputValue(endDate);

  // Immediately emit the change
  emitDateChange();
};

// Picker visibility management
const showDatePicker = (): void => {
  if (isDisabled.value) return;
  isPickerVisible.value = true;
};

const hideDatePicker = (): void => {
  isPickerVisible.value = false;
};

const toggleDatePicker = (): void => {
  if (isPickerVisible.value) {
    hideDatePicker();
  } else {
    showDatePicker();
  }
};

// Handle input click with event prevention
const handleInputClick = (event: MouseEvent): void => {
  event.stopPropagation();
  if (isDisabled.value) return;

  if (!isPickerVisible.value) {
    showDatePicker();
  }
};

// Handle toggle button click with event prevention
const handleToggleClick = (event: MouseEvent): void => {
  event.stopPropagation();
  toggleDatePicker();
};

// Keyboard handling
const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    toggleDatePicker();
  } else if (event.key === 'Escape') {
    hideDatePicker();
  }
};

// Click outside to close
const handleClickOutside = (event: MouseEvent): void => {
  if (!isPickerVisible.value) return;

  const inputContainer = (displayInput.value as HTMLElement | null)?.closest('.date-range-input-container');
  const pickerContainer = datePicker.value;

  // Check if click is outside both input container and picker
  if (inputContainer && pickerContainer &&
      !inputContainer.contains(event.target as Node) &&
      !pickerContainer.contains(event.target as Node)) {
    hideDatePicker();
  }
};

// Lifecycle
onMounted(() => {
  updateDateInputs();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Watch for external value changes
watch(currentValue, () => {
  updateDateInputs();
});
</script>

<style scoped>
.date-range-component {
  position: relative;
  width: 100%;
}

.form-label {
  display: block;
  margin-bottom: 1rem;
  font-weight: 500;
  color: #212529;
  font-size: 1rem;
}

.date-range-input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.date-range-input-container:focus-within {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.date-range-input-container.is-disabled {
  background-color: #e9ecef;
  opacity: 0.65;
}

.date-range-input-container.is-invalid {
  border-color: #dc3545;
}

.date-range-icon {
  padding: 0.75rem 1rem;
  color: #6c757d;
  background-color: #e9ecef;
  border-right: 1px solid #ced4da;
  font-size: 0.875rem;
}

.date-range-display {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  outline: none;
  background: transparent;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1.5;
}

.date-range-display:disabled {
  cursor: not-allowed;
}

.date-range-toggle {
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: color 0.15s ease-in-out;
  font-size: 0.875rem;
}

.date-range-toggle:hover:not(:disabled) {
  color: #495057;
}

.date-range-toggle:disabled {
  cursor: not-allowed;
}

.date-picker-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1050;
  margin-top: 0.125rem;
  padding: 1rem;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.predefined-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.date-inputs {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.date-input-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date-input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
}

.date-input {
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  font-size: 1rem;
  line-height: 1.5;
}

.date-input:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  outline: none;
}

.date-separator {
  color: #6c757d;
  font-weight: 500;
  font-size: 0.875rem;
  margin-top: 1.5rem;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #dee2e6;
  margin-top: 0.5rem;
}

.validation-message {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .predefined-buttons {
    justify-content: center;
  }
  
  .date-inputs {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .date-separator {
    margin-top: 0;
    align-self: center;
  }
  
}
</style>
